"use client";

// WO-COMMERCE-BIND-001 §16 · DoD 10 (REDO) — THE HOST HALF OF THE EXPORT'S OWN CHECKOUT.
//
// The founder's verdict on the first attempt was NOT ACCEPTED: "no that's not the checkout — it's
// similar but not it." He was right; that pass restyled the parent's six-step skeleton in export
// tokens. Right paint, wrong bones. The customer checkout IS the export's own `page:'pay'` flow —
// PAYMENT → DETAILS → REVIEW → CONFIRMED — and this component is what feeds it and what answers it.
//
// ═══ HOW IT WORKS, AND WHY NOTHING NEW WAS INVENTED TO DO IT ═══
//  • THE CART GOES IN THROUGH THE `?dc=` PAYLOAD the export already reads. Measured before writing a
//    line of this: rendering the served export with `?dc={page:'pay',cart:[…]}` drove `payLines`, the
//    sidebar total and the review card from the injected lines. So the real cart needs no new channel
//    — only a client component, because the cart lives in browser storage and the server cannot see it.
//  • THE ANSWER COMES BACK ON THE CHANNEL DoD 7 ALREADY BUILT (`ExportUrlSync`): same-origin
//    `postMessage`, origin pinned in both directions. The export asks with `sg:pay`; this replies with
//    `sg:payresult`. A `"*"` targetOrigin would hand a customer's booking to whatever framed us.
//  • THE SUBMISSION IS THE ONE FROM DoD 3 (`fafa4ca`): POST /api/legacy/booking/submit, which resolves
//    the numeric `midentifier` server-side, builds live's own request shape, and STAGES unless the
//    founder arms it. `booked` is decided by `result.orderId` — never by a 200.
//
// ═══ THE HONESTY RAIL THIS COMPONENT ENFORCES ═══
// The export cannot reach CONFIRMED on its own any more (the rail script removed the mint). It reaches
// it only when this component reports a real `orderId`. Staged, refused and failed all come back as a
// reason string that the export shows in its own notice banner, on the REVIEW step, where the customer
// is standing. There is no path from this file to a success screen without an order behind it.

import { useEffect, useMemo, useRef } from "react";
import DesignSurface from "@/components/site/DesignSurface";
import { useCartStore } from "@/lib/cart-store";
import { gaItemFromDesignLine, reportReviewCart } from "@/lib/analytics-event";
import { CONSENT_GRANTED_EVENT } from "@/lib/consent";
import type { CartItem } from "@/lib/cart-store";
import { isDepartLine as isDepart } from "@/lib/booking-pay-bridge";
import type { PayOrder } from "@/lib/booking-pay-bridge";
import { resolveDesignPay } from "@/lib/design-pay";
import InpageCardPayment from "./InpageCardPayment";
import { useDesignCartLines } from "@/components/marketplace/DesignCartLines";
import { mergeDesignLegs } from "./BookingSteps";

/** The export's own cart-line shape, read from its `payLines` / cart renderer. */
type ExportLine = {
  id: string;
  name: string;
  cat: string;
  vendor: string;
  qty: number;
  price: number;
  /** the guest fare — the export's savings maths reads `guest` and `member`, not `price` alone */
  guest: number;
  member?: number;
  img?: string;
  date?: string;
  legKind?: string;
  legName?: string;
  klass?: string;
  dep?: string;
  arr?: string;
  depStop?: string;
  arrStop?: string;
  tripId?: string;
  journey?: "round-trip" | "one-way";
  /** FOUNDER-QA-FOLLOWUP-008 · item 2 — the selected ticket's own published luggage terms (engine schedule-search). */
  luggage?: string[];
  opId?: string;
  /**
   * The hosted mountain the design stamped on a live-feed product when it was added (SG_LIVE_STORE_LINE). The cart's owner
   * (`cartOwnerMountain`) reads it; this reader must carry it exactly as recorded - never infer it from the route.
   */
  mountainCode?: string;
  /** a store line's own dates / time, carried verbatim */
  start?: string;
  end?: string;
  time?: string;
  /**
   * SNOWCLUB-LAUNCH-REMAINING-PARITY-001 — the schedule identity and SearchCriteriaDTO fields
   * `commitRide` stamps on a design-held leg. This reader must not drop them: the design re-persists
   * `sg-cart-v1` from the state it was BOOTED with (see the `dc`/`checkoutCart` note below on this
   * file, and `s.cart`'s own re-save effects in the export html), so a line these fields never
   * survived through would have them silently erased from storage on the next save, breaking the
   * round trip back to `mergeDesignLegs` even though nothing there changed.
   */
  sgSid?: string | number;
  departureCode?: string;
  arrivalCode?: string;
  travelDate?: string;
  returnDate?: string;
  adult?: number;
  child?: number;
  senior?: number;
  student?: boolean;
  noStore?: boolean;
  returnTrip?: boolean;
  /**
   * SNOWCLUB-V101-STAGING-A1 — the fare basis `commitRide` priced the leg on (`flexi: !!s.tFlex`) and the
   * rate it was priced for (`who`). `planDesignOrder` refuses a transport line without a boolean `flexi`,
   * so a reader that drops it turns every fresh pick into "please pick it again".
   */
  flexi?: boolean;
  who?: "guest" | "member";
};

/**
 * FOUNDER-QA-FOLLOWUP-008 · item 3 — a line persisted by the design (`sg-cart-v1`) keeps WHAT IT IS. This reader used to
 * stamp every persisted line `cat: "Transport"` with a bus image, so a store product (e.g. Vallea Lumina) reached MY CART
 * titled by its supplier, filed under TRANSPORT and priced "per seat". A line is transport only when it says so (its own
 * `cat`, or a leg kind); otherwise its own category, image, dates and time ride through untouched.
 */
export function exportLineFromSaved(line: Record<string, unknown>, n: number): ExportLine {
  const qty = Number(line.qty);
  const price = Number(line.price);
  const guest = Number(line.guest);
  const member = Number(line.member);
  const str = (k: string) => (typeof line[k] === "string" && (line[k] as string) !== "" ? (line[k] as string) : undefined);
  const transport = str("cat") ? str("cat") === "Transport" : !!str("legKind");
  const luggage = Array.isArray(line.luggage)
    ? (line.luggage as unknown[]).filter((x): x is string => typeof x === "string" && x.trim() !== "")
    : [];
  const out: ExportLine = {
    id: String(line.id || `schedule-${n + 1}`),
    name: String(line.name || (transport ? "Bus seat" : "")),
    cat: transport ? "Transport" : (str("cat") ?? "Marketplace"),
    vendor: String(line.vendor || ""),
    qty, price, guest: Number.isFinite(guest) ? guest : price,
    ...(Number.isFinite(member) ? { member } : {}),
  };
  const img = str("img") ?? (transport ? "assets/icons/spot/transport-bus.png" : undefined);
  if (img) out.img = img;
  for (const k of [
    "date", "legKind", "legName", "klass", "dep", "arr", "depStop", "arrStop", "tripId", "opId", "start", "end", "time",
    "departureCode", "arrivalCode", "travelDate", "returnDate", "mountainCode",
  ] as const) {
    const v = str(k);
    if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  }
  if (line.journey === "one-way" || line.journey === "round-trip") out.journey = line.journey;
  if (luggage.length) out.luggage = luggage;
  // SNOWCLUB-LAUNCH-REMAINING-PARITY-001 — the schedule id and the rest of the SearchCriteriaDTO, carried
  // through exactly as recorded: a number-typed pax field is never coerced from a missing one, and a
  // boolean is only set when the saved line actually holds one (an older persisted line has none).
  if (typeof line.sgSid === "string" || typeof line.sgSid === "number") out.sgSid = line.sgSid;
  for (const k of ["adult", "child", "senior"] as const) {
    const v = Number(line[k]);
    if (Number.isFinite(v)) (out as Record<string, unknown>)[k] = v;
  }
  for (const k of ["student", "noStore", "returnTrip", "flexi"] as const) {
    if (typeof line[k] === "boolean") (out as Record<string, unknown>)[k] = line[k];
  }
  // SNOWCLUB-V101-STAGING-A1 — the fare basis and rate the leg was priced on survive the round trip as
  // recorded; an older line that never held them stays without them (and is refused at pay, not guessed).
  if (line.who === "guest" || line.who === "member") out.who = line.who;
  return out;
}

/** Merge the design's schedule/store cart with the host cart. Once the design has persisted its cart,
 * that record owns transport selection: a host transport id absent from it was removed and must not
 * be resurrected on the next review-page load. Host marketplace lines and matching enrichments remain. */
export function mergeCheckoutLines(saved: ExportLine[], hosted: ExportLine[], persistedCartPresent = false): ExportLine[] {
  const merged = new Map(saved.map((line) => [line.id, line]));
  hosted.forEach((line) => {
    if (persistedCartPresent && line.cat === "Transport" && !merged.has(line.id)) return;
    // a host enrichment of the same exact id must not erase the design's recorded mountain stamp (its only owner evidence)
    const stamp = merged.get(line.id)?.mountainCode;
    merged.set(line.id, stamp && !line.mountainCode ? { ...line, mountainCode: stamp } : line);
  });
  return Array.from(merged.values());
}

type BookingContactDraft = {
  info: { first?: string; last?: string; email?: string; phone?: string; same?: string; tFirst?: string; tLast?: string };
  notes: string;
};

/**
 * The Kit's `pax` state is one traveller count for the journey, not the sum of
 * its legs. A round trip contains two transport lines with the same selected
 * seat quantity, so take the first valid transport line rather than adding
 * both and turning one rider into two on Details.
 */
export function selectedTravellerCount(lines: readonly { cat?: string; qty?: number }[]): number | undefined {
  const line = lines.find((candidate) => candidate.cat === "Transport" && typeof candidate.qty === "number" && Number.isFinite(candidate.qty) && candidate.qty > 0);
  const quantity = line?.qty;
  return typeof quantity === "number" ? Math.floor(quantity) : undefined;
}

/** Matches the Kit's existing per-tab draft key. The current host state supplies the mountain code
 * rather than the display-name lookup the Kit uses, so that lookup intentionally resolves to its
 * established empty segment; changing it would strand drafts already created by Details. */
function tripTag(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) hash = (hash * 31 + value.charCodeAt(index)) % 1000003;
  return hash.toString(36);
}

function readBookingContactDraft(cart: ExportLine[]): BookingContactDraft | null {
  if (typeof window === "undefined") return null;
  const journey = cart.find((line) => line.tripId)?.tripId;
  if (!journey) return null;
  try {
    const raw = JSON.parse(window.sessionStorage.getItem(`sg-booking-pax-v1::${tripTag(journey)}`) || "null") as {
      v?: unknown; exp?: unknown; info?: unknown; notes?: unknown;
    } | null;
    const expiresAt = typeof raw?.exp === "number" ? raw.exp : Number.NaN;
    if (!raw || raw.v !== 1 || !Number.isFinite(expiresAt) || expiresAt < Date.now() || !raw.info || typeof raw.info !== "object") return null;
    const source = raw.info as Record<string, unknown>;
    const info: BookingContactDraft["info"] = {};
    // QA-BATCH-2 · item 14 — the traveller choice ("same" = purchaser or "0" = someone else) and the other
    // traveller's name are booking contact fields too; they ride in the same short-lived draft.
    for (const field of ["first", "last", "email", "phone", "same", "tFirst", "tLast"] as const) {
      if (typeof source[field] === "string") info[field] = source[field].slice(0, 160);
    }
    return { info, notes: typeof raw.notes === "string" ? raw.notes.slice(0, 1200) : "" };
  } catch {
    return null;
  }
}

export default function ExportCheckout({
  mountainCode,
  step,
  dc,
  vw,
  store,
}: {
  mountainCode: string;
  /**
   * FOUNDER-WALK-QA-STAGING-007 · A4 REMAINDER — WHICH OF THE THREE URLS THIS IS. Measured on the
   * served build at 88d6de69, /booking/details, /booking/store and /booking/review all rendered the
   * SAME screen (PAYMENT, step 1 of the design's pay flow) — three addresses, one page, and the
   * stepper's own labels (DETAILS · MARKETPLACE · REVIEW & PAY) promising three different ones.
   * The URL now selects the design state it names: details → the pay flow at its first step,
   * store → the design's marketplace page for this mountain (the in-funnel store the stepper
   * links to), review → the pay flow at REVIEW. An EMPTY cart on any of the three renders the
   * design's cart page instead, whose own empty state ("Your cart is empty.") is the truthful
   * answer — a payment screen totalling $0 with a live CONTINUE was the alternative, and it was
   * what shipped.
   *
   * SG-SITEWIDE-PERFORMANCE-RENDER-REPAIR-001-IMPL-010 — the URL→state selection above now happens
   * on the SERVER (the route builds `dc`), because the design is server-rendered here like every
   * other family. The server cannot read the cart, so the empty-cart half of the rule moved into
   * the design itself: SG_HOST_CART_SYNC (design-sources.ts) applies the host's merged cart after
   * hydration and, on the REVIEW boot only, downgrades a truly-empty cart to the design's cart
   * page. That scoping is deliberate and argued in the patch: the A4 guard is against a $0 payment
   * screen, which only the pay rail can be; for an empty cart the Booking Store is itself the
   * truthful, usable answer, where the cart page is a dead end — and the measured cold-mobile cost
   * of the page swap was the 13–15 s footer-art LCP this packet removes.
   */
  step: "details" | "store" | "review";
  /**
   * SG-SITEWIDE-PERFORMANCE-RENDER-REPAIR-001-IMPL-010 — the design's boot payload, built on the
   * SERVER by the route (page state for the URL + live commerce facts, NO cart: the cart lives in
   * browser storage and a server-booted cart would be a lie — the measured hazard this change was
   * blocked on). Base64 of the same shape ExportShell hands every other design route.
   */
  dc: string;
  /** The route's UA-derived viewport guess, same as ExportShell (server render must match hydration). */
  vw: number;
  /**
   * IMPL-010 — the live store catalogue for the Booking Store steps (the same JSON /api/legacy/store
   * answers), server-resolved so the grid is complete in the served HTML; null when the live read
   * failed, in which case the design's own client fetch takes over exactly as before.
   */
  store?: unknown;
}) {
  const items = useCartStore((s) => s.items);
  // ── TWO LISTS, ON PURPOSE — founder directive 2026-08-28, and the split is the whole repair ─────
  //
  // `legs` is TRANSPORT ONLY and feeds the LEGACY BOOKING SUBMISSION below. That must not widen: the
  // legacy engine books buses, a marketplace line has no departure to book, and LEGACY_BOOKING_SUBMIT
  // stays unset by founder ruling regardless.
  //
  // `shown` is EVERY line, and it is what the customer reads. Until now this component filtered to
  // transport for BOTH jobs, so a marketplace cart reached /mountain-<m>/booking/review and the
  // export was handed an EMPTY cart — MEASURED 2026-08-28 on the served build: cart holds 1 line,
  // the review page renders 0 <main> and 14 characters of text ("MEMBER PREVIEW"). A visitor could
  // not see what they were buying because there was nothing on the page at all.
  //
  // (That also exposed a false positive in my own gate: CHECKOUT-DOOR's `showsOrder` matched
  // /review/i against the word "PREVIEW" in that pill and reported the blank page as showing an
  // order. The gate is corrected in qa01-redproof.mjs to assert the ITEM's identity instead.)
  //
  // ── SNOWCLUB-LAUNCH-REMAINING-PARITY-001 — `legs` HAD THE SAME BLANK-CART DEFECT AS `shown` DID,
  // ONE LEVEL DEEPER. Since the A4 swap this route is reached with the departure the customer picked
  // living in `sg-cart-v1` (the design's own store), not in `items` (the host `useCartStore`) — `cart`
  // / `checkoutCart` below already read BOTH (via `selectedScheduleState`, `sg-cart-v1` parsed for
  // DISPLAY), so REVIEW correctly showed the line and its $34 fare. `legs` never did: it stayed
  // `items.filter(transport)` alone, so the SUBMISSION this feeds (`resolvePayResult` below) saw an
  // empty cart on every booking made through the only path that now exists. MEASURED on the served
  // candidate: PAY, fully filled in, on a cart the screen showed as $37 total — the honesty rail's
  // own empty-cart reason came back, because `legs.length` was 0.
  //
  // `mergeDesignLegs` (the same function `BookingSteps`'s results leg already uses for the identical
  // problem — "the results leg commits into sg-cart-v1, not into the host store") is reused rather
  // than reimplemented: a second copy of a cart merge is the exact class of drift the comment above
  // already warns about for `resolvePayResult` itself. It stays TRANSPORT ONLY — `mergeDesignLegs`
  // filters both its inputs to `cat === "Transport"` / `section === "transport"` before merging — so
  // the founder directive above is unchanged; only the SOURCE of a transport leg widened, not the kind.
  //
  // SNOWCLUB-V101-STAGING-A1 — `legs` NO LONGER FEEDS ANY SUBMISSION. The legacy booking submission it
  // fed is removed from the pay seam (see THE PAY BRIDGE below); `resolveDesignPay` reads the displayed
  // cart itself. `legs` now only sizes the `data-cart-lines` hook the funnel harnesses read.
  const designLines = useDesignCartLines();
  const legs = useMemo(
    () => mergeDesignLegs(items, designLines, mountainCode),
    [items, designLines, mountainCode],
  );
  // FOUNDER-WALK-QA-STAGING-007 · A4 REMAINDER — DO NOT HAND OVER AN UNHYDRATED CART. Measured on the
  // served build at 88d6de69 with a rental in the persisted cart: all three legs rendered the design's
  // cart page reading "0 items held for Whistler". React's hydration render reads the store's SERVER
  // snapshot (empty) through useSyncExternalStore, so a payload built on the first client render was
  // the empty one, and the real cart arrived one render too late to matter. `hydrated` flips in the
  // store's own onRehydrateStorage; until it is true the cart handover below (SG_CART_SYNC) does not
  // fire. The design itself is mounted immediately — server-rendered, like every other family — and
  // restores its own persisted lines (SG-CART-PERSIST-021) while this component waits for `hydrated`.
  const hydrated = useCartStore((s) => s.hydrated);
  const shown = items;
  // A fare is selected in the export results surface before this route mounts, so its cart is
  // persisted in the export's deliberately separate, non-sensitive `sg-cart-v1` shape. Read that
  // exact shape as the route bridge; the marketplace Zustand cart remains preferred whenever it
  // already has items.
  const selectedScheduleState = useMemo<{ present: boolean; lines: ExportLine[] }>(() => {
    if (typeof window === "undefined") return { present: false, lines: [] };
    try {
      const raw = window.localStorage.getItem("sg-cart-v1");
      const saved = JSON.parse(raw || "null") as { v?: unknown; cart?: unknown } | null;
      const lines: unknown[] = saved?.v === 1 && Array.isArray(saved.cart) ? saved.cart : [];
      return { present: raw !== null && saved?.v === 1 && Array.isArray(saved.cart), lines: lines
        .filter((line: unknown): line is Record<string, unknown> => !!line && typeof line === "object"
          && typeof (line as Record<string, unknown>).id === "string"
          && Number.isFinite(+((line as Record<string, unknown>).qty ?? NaN))
          && +((line as Record<string, unknown>).qty ?? 0) > 0
          && Number.isFinite(+((line as Record<string, unknown>).price ?? NaN)))
        .map((line: Record<string, unknown>, n: number) => exportLineFromSaved(line, n)) };
    } catch {
      return { present: false, lines: [] };
    }
  }, []);
  const selectedScheduleCart = selectedScheduleState.lines;

  // The cart the export renders. Every value is the one the RESULTS screen stamped on the line — the
  // fare a customer was actually quoted — so the checkout cannot quote a different number than the
  // search did. `member` rides along so the export can show what joining would have saved.
  const cart: ExportLine[] = useMemo(
    () =>
      shown.map((i, n) => {
        // The Kit summary has two independent rows: clock times, then stop names.  Keep their
        // meanings intact and omit each incomplete pair rather than presenting a stop as a time.
        // FOUNDER-QA-FOLLOWUP-008 · item 3 — each schedule field travels on its own: a departure time or boarding stop the
        // selected schedule supplied is shown even when its arrival counterpart was not published.
        const scheduleSummary = (i.section ?? "marketplace") === "transport"
          ? {
              ...(typeof i.depTime === "string" && i.depTime ? { dep: i.depTime } : {}),
              ...(typeof i.arrTime === "string" && i.arrTime ? { arr: i.arrTime } : {}),
              ...(typeof i.depStop === "string" && i.depStop ? { depStop: i.depStop } : {}),
              ...(typeof i.arrStop === "string" && i.arrStop ? { arrStop: i.arrStop } : {}),
            }
          : {};
        return {
          id: i.id || `l${n + 1}`,
          name: i.title,
          // The line's OWN axis, not a constant. A marketplace line printed as "Transport" would be
          // the same lie as an empty page, quietly.
          cat:
            (i.section ?? "marketplace") === "transport"
              ? "Transport"
              : i.category === "rentals"
                ? "Rentals"
                : i.category === "activities"
                  ? "Activities"
                  : "Marketplace",
          vendor: i.supplier ?? "",
          qty: i.qty,
          // §18 gap 1 — THE SAVE FIGURE IS COMPUTED, NEVER THE DESIGN'S DEMO CONSTANT. The export
          // derives it itself: `cartPotential = Σ qty × max(0, (guest − member) − (guest − price))`.
          // That needs BOTH `guest` and `member` on the line; the first wired pass sent only `price`
          // and `member`, so `guest` was undefined and the figure was NaN. A guest pays the guest
          // fare, so price === guest here and the potential collapses to the real member delta from
          // live's own fare table. No member fare on the line ⇒ potential 0 ⇒ the banner and the
          // sidebar save line hide themselves, which is exactly §18's rule.
          price: i.guestPrice ?? 0,
          guest: i.guestPrice ?? 0,
          member: i.memberPrice ?? undefined,
          date: i.startDate,
          // DEPART/RETURN is a transport idea. A marketplace line carries no leg, and stamping one
          // would print a direction the purchase does not have.
          ...((i.section ?? "marketplace") === "transport"
            ? {
                legKind: isDepart(i) ? "DEPART" : "RETURN",
                legName: isDepart(i) ? "Departure" : "Return",
                // The Kit's category icon is not a substitute for a supplier logo.
                img: "assets/icons/spot/transport-bus.png",
                tripId: i.tripId,
                journey: i.journey,
              }
            : {}),
          // no `klass`: a cart line does not carry a service class, and the export prints that chip
          // only when one is present. Omitted rather than filled with a plausible "Standard".
          ...scheduleSummary,
        };
      }),
    [shown],
  );
  // FOUNDER-QA-FOLLOWUP-008 · item 2 — a host-store transport line has no terms field; the design's persisted leg of the
  // same kind does (it was picked from the search answer). Joined by leg kind, never by position.
  const legOf = (k?: string) => (k === "DEP" || k === "DEPART" ? "D" : k === "RET" || k === "RETURN" ? "R" : "");
  const withTerms = cart.map((l) => {
    if (l.cat !== "Transport" || l.luggage) return l;
    const twin = selectedScheduleCart.find((x) => x.cat === "Transport" && legOf(x.legKind) && legOf(x.legKind) === legOf(l.legKind) && x.vendor === l.vendor);
    return twin?.luggage ? { ...l, luggage: twin.luggage, ...(twin.opId ? { opId: twin.opId } : {}) } : l;
  });
  const checkoutCart = mergeCheckoutLines(selectedScheduleCart, withTerms, selectedScheduleState.present);
  const selectedPax = selectedTravellerCount(checkoutCart);
  const scheduleTip = useMemo<number | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const saved = JSON.parse(window.localStorage.getItem("sg-cart-v1") || "null") as { v?: unknown; cartTip?: unknown; cartTipSet?: unknown } | null;
      if (saved?.v !== 1 || saved.cartTip == null || !Number.isFinite(+saved.cartTip)) return null;
      // SNOWCLUB-V101-STAGING-A1 — only a MARKED tip is the customer's choice. This host used to hand the
      // design an unmarked $3 default, which the design then saved; reading that back as a choice would
      // refuse the checkout for a tip nobody picked.
      return saved.cartTipSet === true ? +saved.cartTip : null;
    } catch {
      return null;
    }
  }, []);
  const cartHasTransport = checkoutCart.some(
    (line) => line.cat === "Transport" || /bus|shuttle|transfer|seat/i.test(`${line.name} ${line.cat}`),
  );
  // The hosted design's own rule (SG_CART_TIP): a transport cart with no choice yet tips $0 — the
  // server-priced order has no tip line, so a $3 nobody picked would refuse every checkout. A chosen
  // tip, zero or not, is passed through; a nonzero one is then refused honestly by planDesignOrder.
  // (The tip itself no longer travels from here: the design restores a MARKED tip from `sg-cart-v1`
  // with the same rule in SG-CART-PERSIST-021; this figure only sizes the Payment Element below.)
  const checkoutTip = cartHasTransport ? (scheduleTip ?? 0) : 0;
  // FOUNDER CORRECTION 2026-09-25 · item 2 — the review URL takes the card in-page (see the flag in `sgc`).
  const inpageCard = step === "review" && checkoutCart.length > 0;
  // Only sizes the Payment Element before the order exists; the authorized amount is the server's.
  const displayedTotal = checkoutCart.reduce((sum, l) => sum + (Number(l.qty) || 0) * (Number(l.price) || 0), 0) + checkoutTip;
  const contactDraft = useMemo(() => readBookingContactDraft(checkoutCart), [checkoutCart]);
  // QA-BATCH-2 · item 13 — the drawer never opens inside the funnel, so the results leg no longer leaves the
  // "open the drawer on arrival" flag behind. A flag an older document may still hold is cleared, not read.
  useEffect(() => {
    try { window.sessionStorage.removeItem("sg-booking-details-drawer-v1"); } catch { /* storage unavailable */ }
  }, []);

  // ── THE CHECKOUT'S HISTORY · FOUNDER-WALK-CHECKOUT-HISTORY-001 (RETAINED, AND WHY IT IS MOOT) ──
  //
  // This block used to explain `MOUNT_SRC` and a `location.replace` refresh. Both are gone with the
  // frame; the finding they record is kept because it is the reason the payload is FROZEN below
  // rather than re-applied, and a future reader re-introducing a refresh needs to know what it cost.
  //
  // THE MEASUREMENT (base 86cd0bed, 1440 and 390): re-setting `src` on a MOUNTED iframe is a frame
  // NAVIGATION, and a frame navigation writes an entry into the JOINT session history this document
  // shared with the frame. The cart rehydrates from storage after mount, so a customer WITH a cart
  // silently collected a second entry — and their next Back press moved the FRAME back to the empty
  // payload instead of leaving the checkout. The top document never navigated, `popstate` never
  // fired on it, and the tab simply appeared stuck. An EMPTY cart never changed the payload, kept
  // one entry, and went Back correctly, which is why every earlier walk read this surface as healthy.
  //
  // UN-FRAMED, THAT ENTIRE CLASS IS GONE: there is no nested browsing context, so nothing this
  // component does can write a joint-history entry at all. The Back button now does exactly what the
  // funnel's own routes say it does. `ExportUrlSync` was the -007 finding's named suspect and was
  // refuted then (WO-URL-NAV-002 removed every navigation from it); that verdict is unchanged.
  //
  // `checkout: "legacy"` is what tells the export's cart page that this cart has an engine behind
  // it — a marketplace-only cart still gets the phase-1 refusal.
  // ── FOUNDER-WALK-QA-STAGING-007 · A4 REMAINDER — THIS SURFACE IS NO LONGER FRAMED ──────────────
  //
  // THE DEFECT, measured on the served build at 597d2f95, both viewports:
  //
  //     /mountain-whistler/booking/details   200 · iframes 1 · <main> 0 · body text "MEMBER PREVIEW" (14 chars) · h1 none
  //     /mountain-whistler/booking/store     200 · iframes 1 · <main> 0 · body text "MEMBER PREVIEW" (14 chars) · h1 none
  //     /mountain-whistler/booking/review    200 · iframes 1 · <main> 0 · body text "MEMBER PREVIEW" (14 chars) · h1 none
  //
  // Three of the eight booking legs served a BLANK PAGE to a signed-out visitor with an empty cart.
  // The frame mounted with an EMPTY `?dc=` and the design, handed no state, painted nothing.
  //
  // Every other family in this build mounts the design IN THIS DOCUMENT under the 2026-08-13 "NO
  // FRAMES" ruling, and A4 already moved the two RESULTS legs across. These three were the last
  // framed family. They now mount through `ExportShell` like everything else, which is also what
  // gives them the server-painted crawlable layer (an <h1> where there was none) and the sibling
  // layers a framed page could not have — `ExportLegacyLinkBridge` above all, without which the
  // design's own links leak back to www.snowbus.com the moment the frame is removed.
  //
  // WHAT THIS COMPONENT IS NOW: the CART half only. It computes the host's merged cart and hands it
  // to the design AFTER hydration (below). It renders no frame and owns no chrome.
  //
  // ── SG-SITEWIDE-PERFORMANCE-RENDER-REPAIR-001-IMPL-010 · THE PAYLOAD MOVED TO THE SERVER, THE CART
  // DID NOT. Measured on the exact base (staging e032c934, cold mobile LH 12.6.1, 2026-10-02): these
  // three legs were the LAST family on the client-only design boot — no design markup in the served
  // HTML, host visibility:hidden until a React-effect boot, so the lazy footer SVG became LCP at
  // 14 640 ms (/booking/store) and 13 169 ms (/booking/review) while every ExportShell route paints
  // at ~2 s. The route now builds `dc` on the SERVER (page state for the URL + commerce facts) and
  // this component mounts `DesignSurface` ssr, exactly like ExportShell.
  //
  // THE RECORDED HAZARD THAT BLOCKED THIS AS A QUICK PATCH, AND HOW IT IS CLOSED: the design
  // re-persists `sg-cart-v1` from the state it was booted with, so a server-booted EMPTY cart could
  // erase a customer's real cart. The server payload therefore carries NO `cart` key at all. The
  // design's own SG-CART-PERSIST-021 restores its persisted lines at mount (reading storage BEFORE
  // any persist effect can write), and the HOST's merged cart — host store + `sg-cart-v1`, the same
  // `mergeCheckoutLines` merge the frozen payload used to carry — is handed over ONCE, after
  // `hydrated`, through `window.__SG_CART_SYNC` + the "sg:cartsync" event (SG_HOST_CART_SYNC in
  // design-sources.ts). Like a route payload, that merged cart is authoritative; the design deletes
  // the payload after consuming it so a recovery boot falls back to `sg-cart-v1`, never to a stale
  // snapshot. The empty-cart rule (A4: the design's cart page is the truthful answer) is applied by
  // the same patch, only while the customer is still on the screen the server booted.
  //
  // `dc` is constant for the document (server-built, no cart in it), so `DesignSurface`'s boot
  // effect — which depends on `dc` — can never re-boot the design mid-checkout. The freeze ref this
  // replaced existed to pin a client-computed payload; a server prop needs no pinning.
  const syncSent = useRef(false);
  /** the review cart handed to the design by the one-shot sync below, for GA4 view_cart */
  const frozenViewCart = useRef<ExportLine[] | null>(null);
  const viewCartReported = useRef(false);
  useEffect(() => {
    if (!hydrated || syncSent.current) return;
    syncSent.current = true;
    (window as unknown as { __SG_CART_SYNC?: unknown }).__SG_CART_SYNC = {
      v: 1,
      cart: checkoutCart,
      // The hosted design defaults pax to 2. Bind the real selected seat count;
      // a return leg is deliberately not summed here.
      ...(selectedPax ? { pax: selectedPax } : {}),
      ...(contactDraft ? { contact: contactDraft } : {}),
    };
    window.dispatchEvent(new Event("sg:cartsync"));
    // The cart handed over once here IS the cart the visitor sees; a later store update never reaches the
    // screen (the sync is one-shot), so it is not a new view either.
    if (step === "review" && checkoutCart.length > 0) frozenViewCart.current = checkoutCart;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  // GA4 view_cart — the review URL shows MY CART with the cart synced above. Once per mounted view: not on
  // the server render, not before hydration (the sync effect, declared first, has run by then), not on an
  // empty cart (page:"cart" empty state), and not again on a card-step re-render or a store update, which
  // change neither the screen nor this ref. A reload or a fresh visit is a new view and reports again. Gates live in @/lib/analytics-event.
  // GA4 begin_checkout — the pay rail opened with a real cart. Once per cart content per session (a reload
  // of the same review page is not a second checkout); host/tag gates live in @/lib/analytics-event.
  // Both go through reportReviewCart, view_cart first, so GA4 sees the funnel in order. The ref is set only
  // once view_cart was actually SENT: a visitor who taps Accept while already on this cart is reported then,
  // via the banner's CONSENT_GRANTED_EVENT — no polling, and nothing before consent.
  const checkoutFingerprint = checkoutCart.map((l) => `${l.id}x${l.qty}`).join("|");
  useEffect(() => {
    if (!hydrated || step !== "review" || checkoutCart.length === 0) return;
    const report = () => {
      viewCartReported.current = reportReviewCart(
        (frozenViewCart.current ?? []).map(gaItemFromDesignLine),
        checkoutCart.map(gaItemFromDesignLine),
        viewCartReported.current,
      );
    };
    report();
    window.addEventListener(CONSENT_GRANTED_EVENT, report);
    return () => window.removeEventListener(CONSENT_GRANTED_EVENT, report);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, step, checkoutFingerprint]);

  // THE FRAME-REFRESH EFFECT THAT USED TO LIVE HERE IS GONE, because the frame it re-pointed is
  // gone. It existed so a cart change reached the design without pushing a history entry (the
  // `location.replace` note above records the Back-button defect that motivated it). Un-framed the
  // same job would mean re-booting `DesignSurface` mid-checkout, which is a worse outcome than a
  // stale line: the customer loses the step they are on. The payload is frozen instead — see the
  // block above `dc` — and the note is kept rather than deleted so the next reader knows the
  // Back-button trap was handled, not overlooked.

  // ── THE PAY BRIDGE — ONE COPY, IN `@/lib/design-pay` ────────────────────────────────────────────
  // SNOWCLUB-V101-STAGING-A1: this used to answer through `resolvePayResult` (the legacy booking
  // engine, plus a labelled fake confirmation on staging). It now answers through `resolveDesignPay`,
  // shared with `BookingPayBridge`: the displayed cart becomes a SERVER-PRICED order and the card is
  // taken by Stripe's hosted PaymentElement on the checkout app. `items` is passed only so a displayed
  // marketplace line can be joined, by exact id, to its backend product.
  //
  // FOUNDER CORRECTION 2026-09-25 · item 2 — NOT on the review URL. There, `InpageCardPayment` is the one
  // answerer: it creates the same server-priced order (`createDesignOrder`, the shared first half of
  // `resolveDesignPay`) and takes the card on this page instead of navigating to the checkout app. Two
  // answerers on one channel would create two orders for one press.
  useEffect(() => {
    if (typeof window === "undefined" || inpageCard) return;

    const onMessage = async (ev: MessageEvent) => {
      // Same-origin only, and only the one shape. The export is served from this origin, so anything
      // else on this channel is another frame trying to drive a booking.
      if (ev.origin !== window.location.origin) return;
      const d = ev.data as { type?: string; order?: PayOrder } | null;
      if (!d || d.type !== "sg:pay") return;
      const result = await resolveDesignPay({ mountainCode, hostItems: items, order: d.order ?? {} });
      // Un-framed, the design's `window.parent.postMessage` IS a post to this window, so the reply
      // goes back the same way — the one line `BookingPayBridge` already changed for the results
      // legs. Origin still pinned: a `"*"` reply would hand a customer's booking to anything else
      // listening on this channel.
      window.postMessage({ type: "sg:payresult", result }, window.location.origin);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [items, mountainCode, inpageCard]);

  // NOT `position: fixed`. The fixed inset:0 sheet the frame sat in capped `document.scrollHeight`
  // at the viewport on every design route — the measured root cause of the footer-containment
  // defect (QA-BATCH-01 rows 6/9, 2026-08-27). In normal flow the design's own content extends the
  // document, which becomes the one scroller, and that is also what lets the 390px overflow gate
  // read a truthful `scrollWidth`. `ExportShell` emits the same relative wrapper for every other
  // route; the shapes are deliberately identical.
  //
  // `data-export-checkout` and `data-cart-lines` are KEPT on the host element. They are what the
  // funnel harnesses and `export-checkout-rail.mjs` identify this surface by — moving the mount
  // without carrying the hooks across would have made those gates silently stop finding anything,
  // which reads as a pass.
  return (
    <div
      data-export-checkout
      data-cart-lines={legs.length}
      style={{ position: "relative", width: "100%", minHeight: "100vh" }}
    >
      <style>{"body{margin:0}"}</style>
      {/* IMPL-010: server-rendered and hydrated, like every ExportShell route. `path` is the real
          route path — the design's own hosted logic gates on location (SG_FUNNEL_REVIEW et al.), and
          the server stand-in must answer those gates the way the browser will. */}
      <DesignSurface dc={dc} vw={vw} ssr path={`/mountain-${mountainCode}/booking/${step}`} store={store} />
      {inpageCard ? (
        <InpageCardPayment mountainCode={mountainCode} hostItems={items} displayedTotal={displayedTotal} />
      ) : null}
    </div>
  );
}
